import { useCallback, useEffect, useMemo, useState } from 'react'

import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { deDE, plPL, enUS, frFR } from '@mui/x-date-pickers/locales'
import dayjs from 'dayjs'
import 'dayjs/locale/de'
import 'dayjs/locale/en'
import 'dayjs/locale/fr'
import 'dayjs/locale/pl'
import { pick, pickBy } from 'lodash-es'
import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { connect, DataItem, load, navigateTo } from 'scrivito'

import { PhoneCallLogToast } from './LogPhoneCallToast'
import { FieldLabel } from '@/Atomic/Atoms/FieldLabel'
import { Button } from '@/Atomic/Molecules/Button'
import { DateTimePicker } from '@/Atomic/Molecules/Inputs/DateTimePicker'
import { AsyncSelect } from '@/Atomic/Molecules/Inputs/Select/AsyncSelect'
import { Textarea } from '@/Atomic/Molecules/Inputs/Textarea'
import { CustomField } from '@/Atomic/Organisms/CustomField'
import {
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
} from '@/Atomic/Templates/Drawer'
import { Form, FormBody, FormFields, FormGroup } from '@/Atomic/Templates/Form'
import { DataClassFollowUpActivityDrawer } from '@/Atomic/Widgets/DataClassDrawer/DataClass.FollowUpActivityDrawer'
import { errorToast } from '@/Components/Toast'
import { Activity } from '@/Data/Activity/ActivityDataClass'
import { ActivityContactRelation } from '@/Data/ActivityContactRelation/ActivityContactRelationDataClass'
import { DataClassName } from '@/Data/const'
import { Person } from '@/Data/Person/PersonDataClass'
import { WhoAmI } from '@/Data/WhoAmI/WhoAmIDataItem'
import { useAsyncSelect } from '@/hooks/useAsyncSelect'
import { useDataClassFormHandler } from '@/utils/CRUD/hooks/useDataClassFormHandler'
import {
  getDataClassFieldMap,
  getDataClassFormMap,
} from '@/utils/CRUD/read/dataClass'
import { FieldMap } from '@/utils/CRUD/read/dataClass/types'
import { getSchemaAttributes } from '@/utils/CRUD/read/schema'
import { getDataItemTitle, getSchemaName } from '@/utils/dataClassMap'

import './PhoneCallLogDrawer.scss'

const PHONE_CALL_ACTIVITY_TYPE = 'CALL'
const OUTGOING_CALL_SUBTYPE = 'OUT_CALL'
const CALL_STATUS_BUSY = 'PSA_STA_CAL_BSY'
const CALL_STATUS_DIAL_TONE = 'PSA_STA_CAL_DTN'
const CALL_STATUS_TAKEN_PLACE = 'PSA_STA_CAL_DON'

const MAIN_TYPE_ATTRIBUTE_CANDIDATES: [string, ...string[]] = [
  'type',
  'activityType',
  'mainType',
]
const SUB_TYPE_ATTRIBUTE_CANDIDATES: [string, ...string[]] = [
  'subType',
  'activitySubType',
]

// Kept in sync with the `endTimeHint` translation.
const END_TIME_REFRESH_INTERVAL_MS = 60_000

type EnumOption = { label: string; value: string }

interface PhoneCallLogDrawerProps {
  contextItem: DataItem
  isOpen: boolean
  close: () => void
}

const datePickerLocaleTextMap = {
  de: deDE.components.MuiLocalizationProvider.defaultProps.localeText,
  pl: plPL.components.MuiLocalizationProvider.defaultProps.localeText,
  en: enUS.components.MuiLocalizationProvider.defaultProps.localeText,
  fr: frFR.components.MuiLocalizationProvider.defaultProps.localeText,
}

const dateTimeDisplayFormatMap = {
  de: 'ddd, D. MMM [—] HH:mm:ss',
  pl: 'ddd, D MMM [—] HH:mm:ss',
  en: 'ddd, MMM D [—] h:mm:ssa',
  fr: 'ddd D MMM [—] HH:mm:ss',
}

const includesAny = (value: string, terms: string[]) => {
  const lower = value.toLowerCase()
  return terms.some((term) => lower.includes(term))
}

const findFieldByName = (fields: FieldMap[], candidates: string[]) => {
  const candidateSet = new Set(
    candidates.map((candidate) => candidate.toLowerCase()),
  )
  return fields.find((field) => candidateSet.has(field.name.toLowerCase()))
}

/**
 * Resolves an enum option value by trying preferred values first, then
 * falling back to the first option matching a predicate.
 *
 * @param options - The available enum options, or `undefined` if not yet loaded.
 * @param preferredValues - Ordered list of well-known values to try first.
 * @param fallbackPredicate - Used when no preferred value is found; returns
 *   the first matching option's value.
 * @returns The resolved value, or `preferredValues[0]` if nothing matches.
 */
const resolveOptionValue = (
  options: EnumOption[] | undefined,
  preferredValues: [string, ...string[]],
  fallbackPredicate: (option: EnumOption) => boolean,
): string => {
  const foundPreferred = preferredValues.find((preferredValue) =>
    options?.some((option) => option.value === preferredValue),
  )

  if (foundPreferred) {
    return foundPreferred
  }

  return options?.find(fallbackPredicate)?.value || preferredValues[0]
}

const getDurationInSeconds = (start: unknown, end: unknown) => {
  if (typeof start !== 'string' || typeof end !== 'string') return 0

  const startDate = new Date(start)
  const endDate = new Date(end)

  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return 0
  }

  return Math.max(
    Math.round((endDate.getTime() - startDate.getTime()) / 1000),
    0,
  )
}

export const PhoneCallLogDrawer: React.FC<PhoneCallLogDrawerProps> = connect(
  ({ contextItem, isOpen, close }) => {
    const { t, i18n } = useTranslation()
    // Prefill is disabled: all defaults are set explicitly in initialize()
    // when the drawer opens. The backend prefill would overwrite those values
    // via reset() because initialize() deliberately does not mark them as dirty.
    const { methods } = useDataClassFormHandler(Activity)
    const { getValues, watch, reset, setValue, setFocus, register, formState } =
      methods
    // Read during render on purpose: `formState` is a proxy that only starts
    // tracking a property once it has been accessed while rendering. Reading
    // it inside the save handler alone would yield a stale, possibly empty
    // object, and the update payload depends on it.
    const { dirtyFields } = formState
    const currentLocale = (i18n.resolvedLanguage || 'en').split('-')[0] || 'en'
    const datePickerLocale: keyof typeof datePickerLocaleTextMap =
      currentLocale in datePickerLocaleTextMap
        ? (currentLocale as keyof typeof datePickerLocaleTextMap)
        : 'en'
    const dateTimeDisplayFormat = dateTimeDisplayFormatMap[datePickerLocale]

    const groups = getDataClassFormMap(Activity, getValues, watch)
    const fields = useMemo(
      () => groups.flatMap((group) => group.fields),
      [groups],
    )

    // The grouped form map omits attributes the backend marks as readonly and
    // not mandatory. Main type and sub type have to be shown in any case, so
    // they are resolved from the schema attributes directly.
    // Not memoized: the lookup reads Scrivito data, which is what `connect()`
    // subscribes to for re-rendering once the schema has loaded. It also
    // re-localizes the attribute titles on a language switch.
    const { schemaAttributes } = getSchemaAttributes(
      getSchemaName(DataClassName.ACTIVITY),
    )
    const findSchemaField = useCallback(
      (candidates: string[]) => {
        for (const candidate of candidates) {
          const fieldMap = getDataClassFieldMap(
            candidate,
            schemaAttributes,
            getValues,
            watch,
          )
          if (fieldMap) return fieldMap
        }

        return undefined
      },
      [getValues, schemaAttributes, watch],
    )

    const subjectField = useMemo(
      () => findFieldByName(fields, ['subject', 'title']),
      [fields],
    )
    const mainTypeField = useMemo(
      () =>
        findSchemaField(MAIN_TYPE_ATTRIBUTE_CANDIDATES) ??
        findFieldByName(fields, MAIN_TYPE_ATTRIBUTE_CANDIDATES),
      [fields, findSchemaField],
    )
    const subTypeField = useMemo(
      () =>
        findSchemaField(SUB_TYPE_ATTRIBUTE_CANDIDATES) ??
        findFieldByName(fields, SUB_TYPE_ATTRIBUTE_CANDIDATES),
      [fields, findSchemaField],
    )
    const beginAtField = useMemo(
      () => findFieldByName(fields, ['beginAt', 'startAt', 'start']),
      [fields],
    )
    const endAtField = useMemo(
      () => findFieldByName(fields, ['endAt', 'finishedAt', 'finishAt', 'end']),
      [fields],
    )
    const statusField = useMemo(
      () => findFieldByName(fields, ['status']),
      [fields],
    )
    const noteField = useMemo(
      () => findFieldByName(fields, ['note', 'notes', 'description']),
      [fields],
    )
    const agentField = useMemo(
      () => findFieldByName(fields, ['agent', 'owner', 'assignedTo']),
      [fields],
    )

    const personReferenceField = useMemo(
      () =>
        fields.find((field) => {
          if (field.type !== 'reference') return false

          const fieldName = field.name.toLowerCase()
          if (
            fieldName === 'agent' ||
            fieldName === 'createdby' ||
            fieldName === 'updatedby'
          ) {
            return false
          }

          return fieldName.includes('person') || fieldName.includes('contact')
        }),
      [fields],
    )
    const subjectFieldName = subjectField?.name || 'subject'
    const mainTypeFieldName =
      mainTypeField?.name || MAIN_TYPE_ATTRIBUTE_CANDIDATES[0]
    const subTypeFieldName =
      subTypeField?.name || SUB_TYPE_ATTRIBUTE_CANDIDATES[0]
    const beginAtFieldName = beginAtField?.name || 'beginAt'
    const endAtFieldName = endAtField?.name || 'endAt'
    const statusFieldName = statusField?.name || 'status'
    const agentFieldName = agentField?.name || 'agent'
    const noteFieldName = noteField?.name
    const personReferenceFieldName = personReferenceField?.name

    const statusOptions = statusField?.enumValueOptions as
      | EnumOption[]
      | undefined
    const mainTypeOptions = mainTypeField?.enumValueOptions as
      | EnumOption[]
      | undefined
    const subTypeOptions = subTypeField?.enumValueOptions as
      | EnumOption[]
      | undefined

    const mainTypeValue = useMemo(
      () =>
        resolveOptionValue(
          mainTypeOptions,
          [PHONE_CALL_ACTIVITY_TYPE],
          (option) => {
            return (
              includesAny(option.value, ['call', 'phone']) ||
              includesAny(option.label, ['call', 'phone', 'telefon'])
            )
          },
        ),
      [mainTypeOptions],
    )

    const outgoingSubTypeValue = useMemo(
      () =>
        resolveOptionValue(
          subTypeOptions,
          [OUTGOING_CALL_SUBTYPE],
          (option) => {
            return (
              includesAny(option.value, ['out', 'call']) ||
              includesAny(option.label, [
                'outgoing',
                'ausgehend',
                'sortant',
                'wychodz',
              ])
            )
          },
        ),
      [subTypeOptions],
    )

    const busyStatusValue = useMemo(
      () =>
        resolveOptionValue(statusOptions, [CALL_STATUS_BUSY], (option) => {
          return (
            includesAny(option.value, ['bsy', 'busy']) ||
            includesAny(option.label, ['busy', 'besetzt', 'occup'])
          )
        }),
      [statusOptions],
    )

    const dialToneStatusValue = useMemo(
      () =>
        resolveOptionValue(statusOptions, [CALL_STATUS_DIAL_TONE], (option) => {
          return (
            includesAny(option.value, ['dtn', 'dial']) ||
            includesAny(option.label, [
              'dial',
              'freizeichen',
              'ring',
              'tonalité',
            ])
          )
        }),
      [statusOptions],
    )

    const takenPlaceStatusValue = useMemo(
      () =>
        resolveOptionValue(
          statusOptions,
          [CALL_STATUS_TAKEN_PLACE],
          (option) => {
            return (
              includesAny(option.value, ['don', 'done']) ||
              includesAny(option.label, [
                'taken place',
                'stattgefunden',
                'réalisé',
                'odbyte',
                'done',
              ])
            )
          },
        ),
      [statusOptions],
    )

    /**
     * Builds a minimal enum field map so main type and sub type are rendered
     * even when the Activity schema does not expose the attribute at all.
     */
    const buildFallbackTypeFieldMap = (
      fieldName: string,
      value: string,
      label: string,
    ): FieldMap => ({
      fieldKey: fieldName,
      name: fieldName,
      type: 'enum',
      enumValueOptions: [{ value, label }],
    })

    // The drawer uses the labels from the design instead of the generic schema
    // titles, so they stay stable regardless of the backend wording.
    const subjectFieldMap = subjectField && {
      ...subjectField,
      title: t('components.activity.phoneCall.titleLabel'),
    }
    const mainTypeFieldMap = {
      ...(mainTypeField ??
        buildFallbackTypeFieldMap(
          mainTypeFieldName,
          mainTypeValue,
          t('components.activity.phoneCall.defaultTitle'),
        )),
      title: t('components.activity.phoneCall.mainTypeLabel'),
    }
    const subTypeFieldMap = {
      ...(subTypeField ??
        buildFallbackTypeFieldMap(
          subTypeFieldName,
          outgoingSubTypeValue,
          t('components.activity.phoneCall.outgoingSubType'),
        )),
      title: t('components.activity.phoneCall.subTypeLabel'),
    }

    // Fields the drawer may write back to an existing activity even when the
    // user did not touch them, because logging a call is what changes them.
    // The sub type is deliberately absent: it is prefilled with "outgoing" and
    // would turn an existing incoming call into an outgoing one. It reaches the
    // update through the dirty fields once the user picks a value.
    const callSpecificFieldNames = useMemo(
      () =>
        [endAtFieldName, statusFieldName, noteFieldName].filter(
          (fieldName): fieldName is string => Boolean(fieldName),
        ),
      [endAtFieldName, statusFieldName, noteFieldName],
    )

    const beginAtValue = watch(beginAtFieldName)
    const currentStatusValue = watch(statusFieldName)
    const endAtValue = watch(endAtFieldName)
    const noteValue = noteFieldName ? watch(noteFieldName) : undefined

    const [isEndTimeAuto, setIsEndTimeAuto] = useState(true)
    const [selectedPersonId, setSelectedPersonId] = useState<string | null>(
      null,
    )
    const [selectedPersonLabel, setSelectedPersonLabel] = useState<
      string | undefined
    >()
    const [isSaving, setIsSaving] = useState(false)
    const [followUpOriginActivity, setFollowUpOriginActivity] =
      useState<DataItem | null>(null)
    const [isFollowUpDrawerOpen, setIsFollowUpDrawerOpen] = useState(false)

    const { loadOptions: loadPersonOptions } = useAsyncSelect(Person)

    const resolveInitialPerson = useCallback(async () => {
      if (contextItem.dataClassName() === DataClassName.PERSON) {
        return {
          id: contextItem.id(),
          label: getDataItemTitle(contextItem) || undefined,
        }
      }

      if (contextItem.dataClassName() !== DataClassName.ACTIVITY) {
        return null
      }

      const relation = await load(
        () =>
          ActivityContactRelation.all()
            .transform({
              filters: {
                activity: contextItem.id(),
                contactEntity: 'Person',
              },
              limit: 1,
            })
            .take()[0],
      )

      const personId = relation?.get('contact')
      if (typeof personId !== 'string') return null

      const person = await load(() => Person.get(personId))
      return {
        id: personId,
        label: person ? getDataItemTitle(person) || undefined : undefined,
      }
    }, [contextItem])

    useEffect(() => {
      if (!isOpen) return

      let cancelled = false

      const initialize = async () => {
        reset()
        setIsEndTimeAuto(true)

        const now = new Date().toISOString()

        // Prefilled defaults are deliberately not marked as dirty. Updating an
        // existing activity only writes the call outcome fields and the fields
        // the user edited, so untouched defaults stay out of the payload.
        setValue(
          subjectFieldName,
          t('components.activity.phoneCall.defaultTitle'),
        )
        setValue(mainTypeFieldName, mainTypeValue)
        setValue(subTypeFieldName, outgoingSubTypeValue)
        setValue(beginAtFieldName, now)
        setValue(endAtFieldName, now)
        setValue(statusFieldName, takenPlaceStatusValue)

        const currentUserId = await load(
          () => WhoAmI.get('contactId') as string | null,
        )
        if (typeof currentUserId === 'string') {
          setValue(agentFieldName, currentUserId)
        }

        const initialPerson = await resolveInitialPerson()
        if (cancelled) return

        setSelectedPersonId(initialPerson?.id || null)
        setSelectedPersonLabel(initialPerson?.label)

        if (personReferenceFieldName) {
          setValue(personReferenceFieldName, initialPerson?.id ?? null)
        }

        if (noteFieldName) {
          setTimeout(() => setFocus(noteFieldName), 0)
        }
      }

      void initialize()

      return () => {
        cancelled = true
      }
    }, [
      isOpen,
      reset,
      setValue,
      setFocus,
      t,
      subjectFieldName,
      mainTypeFieldName,
      subTypeFieldName,
      beginAtFieldName,
      endAtFieldName,
      statusFieldName,
      agentFieldName,
      noteFieldName,
      personReferenceFieldName,
      mainTypeValue,
      outgoingSubTypeValue,
      takenPlaceStatusValue,
      resolveInitialPerson,
    ])

    useEffect(() => {
      dayjs.locale(datePickerLocale)
    }, [datePickerLocale])

    useEffect(() => {
      if (!isOpen || !isEndTimeAuto) return

      const intervalId = window.setInterval(() => {
        setValue(endAtFieldName, new Date().toISOString(), {
          shouldDirty: true,
        })
      }, END_TIME_REFRESH_INTERVAL_MS)

      return () => window.clearInterval(intervalId)
    }, [endAtFieldName, isEndTimeAuto, isOpen, setValue])

    useEffect(() => {
      if (!takenPlaceStatusValue) return
      if (typeof noteValue !== 'string') return
      if (!noteValue.trim()) return

      if (currentStatusValue !== takenPlaceStatusValue) {
        setValue(statusFieldName, takenPlaceStatusValue, { shouldDirty: true })
      }
    }, [
      currentStatusValue,
      noteValue,
      setValue,
      statusFieldName,
      takenPlaceStatusValue,
    ])

    const onDrawerClose = () => {
      reset()
      setIsEndTimeAuto(true)
      setSelectedPersonId(null)
      setSelectedPersonLabel(undefined)
      close()
    }

    const onPersonChange = (value: unknown) => {
      const personId = typeof value === 'string' && value ? value : null
      setSelectedPersonId(personId)

      if (personId) {
        load(() => Person.get(personId))
          .then((person) => {
            setSelectedPersonLabel(
              person ? getDataItemTitle(person) || undefined : undefined,
            )
          })
          .catch(() => {
            setSelectedPersonLabel(undefined)
          })
      } else {
        setSelectedPersonLabel(undefined)
      }

      if (personReferenceFieldName) {
        setValue(personReferenceFieldName, personId, { shouldDirty: true })
      }
    }

    const setStatus = (nextStatus?: string) => {
      if (!nextStatus) return
      setValue(statusFieldName, nextStatus, { shouldDirty: true })
    }

    const statusOptionsUi = [
      {
        value: busyStatusValue,
        label: t('components.activity.phoneCall.status.busy'),
      },
      {
        value: dialToneStatusValue,
        label: t('components.activity.phoneCall.status.dialTone'),
      },
      {
        value: takenPlaceStatusValue,
        label: t('components.activity.phoneCall.status.takenPlace'),
      },
    ]
    const endAtRequirementState = endAtField?.fieldSchema?.mandatory
      ? 'required'
      : 'optional'
    const endTimeLabel = t('components.activity.phoneCall.endTimeLabel')

    /**
     * Links the given person to the given activity via an
     * `ActivityContactRelation`, unless that link already exists.
     *
     * Existing relations to other people are left in place: an activity may
     * legitimately involve several contacts, and the drawer has no way to tell
     * an obsolete relation from one that another part of the CRM depends on.
     *
     * @param activityId - ID of the activity to link the person to.
     * @param personId - ID of the person to link, or `null` to do nothing.
     */
    const syncPersonRelations = async (
      activityId: string,
      personId: string | null,
    ) => {
      if (!personId) return

      const existingRelations = await load(() =>
        ActivityContactRelation.all()
          .transform({
            filters: {
              activity: activityId,
              contactEntity: 'Person',
            },
          })
          .take(),
      )

      const alreadyLinked = existingRelations.some(
        (relation) => relation.get('contact') === personId,
      )
      if (alreadyLinked) return

      await ActivityContactRelation.create({
        activity: activityId,
        contact: personId,
      })
    }

    const openFollowUpDrawer = (activity: DataItem) => {
      setFollowUpOriginActivity(activity)
      setIsFollowUpDrawerOpen(true)
    }

    const showNoAnswerToast = (activity: DataItem) => {
      toast(
        <PhoneCallLogToast
          variant="info"
          headline={t('components.activity.phoneCall.toast.noAnswerHeadline')}
          text={t('components.activity.phoneCall.toast.noAnswerText')}
          actionLabel={t('components.activity.phoneCall.toast.createFollowUp')}
          onAction={() => openFollowUpDrawer(activity)}
          secondaryActionLabel={t(
            'components.activity.phoneCall.toast.goToActivity',
          )}
          onSecondaryAction={() => navigateTo(activity)}
        />,
        { autoClose: 7000, position: 'top-right' },
      )
    }

    const showSuccessToast = (
      activity: DataItem,
      durationInSeconds: number,
    ) => {
      toast(
        <PhoneCallLogToast
          variant="success"
          headline={t('components.activity.phoneCall.toast.successHeadline')}
          text={t('components.activity.phoneCall.toast.successText', {
            count: durationInSeconds,
          })}
          actionLabel={t('components.activity.phoneCall.toast.goToActivity')}
          onAction={() => navigateTo(activity)}
        />,
        { autoClose: 7000, position: 'top-right' },
      )
    }

    const showUpdateToast = (activity: DataItem, durationInSeconds: number) => {
      toast(
        <PhoneCallLogToast
          variant="success"
          headline={t('components.activity.phoneCall.toast.updateHeadline')}
          text={t('components.activity.phoneCall.toast.successText', {
            count: durationInSeconds,
          })}
          actionLabel={t('components.activity.phoneCall.toast.goToActivity')}
          onAction={() => navigateTo(activity)}
        />,
        { autoClose: 7000, position: 'top-right' },
      )
    }

    const handleSave = async () => {
      if (isSaving) return

      setIsSaving(true)

      try {
        const values = getValues() as Record<string, unknown>
        // Only update in-place when the context item is a phone-call Activity.
        // Any other Activity type (Meeting, Task, …) must result in a new
        // phone-call Activity instead of corrupting the existing record.
        const isActivityContext =
          contextItem.dataClassName() === DataClassName.ACTIVITY &&
          contextItem.get(mainTypeFieldName) === mainTypeValue
        const payload = pickBy(
          values,
          (value) => value !== undefined,
        ) as Record<string, unknown>

        if (isEndTimeAuto || !payload[endAtFieldName]) {
          payload[endAtFieldName] = new Date().toISOString()
        }

        if (!payload[statusFieldName]) {
          payload[statusFieldName] = takenPlaceStatusValue
        }

        if (noteFieldName && values[noteFieldName] !== undefined) {
          payload[noteFieldName] = values[noteFieldName]
        }

        const savedStatusValue =
          typeof payload[statusFieldName] === 'string'
            ? payload[statusFieldName]
            : ''
        const isNoAnswer =
          savedStatusValue === busyStatusValue ||
          savedStatusValue === dialToneStatusValue

        // A call that did not take place is a separate event, so it never
        // overwrites the activity it was logged from (agreed Option A).
        const isActivityUpdate = isActivityContext && !isNoAnswer

        if (!isActivityUpdate) {
          // Defaults that describe a newly created phone call activity. They
          // must never be applied to an existing activity, which already
          // carries its own title, type, schedule and owner.
          if (!payload[subjectFieldName]) {
            payload[subjectFieldName] = t(
              'components.activity.phoneCall.defaultTitle',
            )
          }

          payload[mainTypeFieldName] = mainTypeValue

          if (!payload[subTypeFieldName]) {
            payload[subTypeFieldName] = outgoingSubTypeValue
          }

          if (!payload[beginAtFieldName]) {
            payload[beginAtFieldName] = new Date().toISOString()
          }

          const currentUserId = await load(
            () => WhoAmI.get('contactId') as string | null,
          )
          if (typeof currentUserId === 'string') {
            payload[agentFieldName] = currentUserId
          }

          if (personReferenceFieldName) {
            payload[personReferenceFieldName] = selectedPersonId
          }
        }

        let savedActivity = contextItem
        if (isActivityUpdate) {
          // Two kinds of fields are written: the call outcome fields, which are
          // always written because logging a call is what changes them, and the
          // fields the user edited. Every other attribute of the activity is
          // left out, so the drawer's prefilled defaults cannot overwrite it.
          const editedFieldNames = Object.keys(dirtyFields)
          await contextItem.update(
            pick(payload, [...callSpecificFieldNames, ...editedFieldNames]),
          )
        } else {
          savedActivity = await Activity.create(payload)
        }

        await syncPersonRelations(savedActivity.id(), selectedPersonId)

        const durationInSeconds = getDurationInSeconds(
          payload[beginAtFieldName],
          payload[endAtFieldName],
        )

        onDrawerClose()

        if (isNoAnswer) {
          showNoAnswerToast(savedActivity)
        } else if (isActivityUpdate) {
          showUpdateToast(savedActivity, durationInSeconds)
        } else {
          showSuccessToast(savedActivity, durationInSeconds)
        }
      } catch (error) {
        errorToast(
          error instanceof Error
            ? error
            : new Error(t('errors.unknownError.headline')),
        )
      } finally {
        setIsSaving(false)
      }
    }

    return (
      <>
        <Drawer
          isOpen={isOpen}
          close={onDrawerClose}
          direction="right"
          closeOnOutsideClick={false}
        >
          <DrawerHeader>
            <span className="phone-call-drawer-heading">
              {t('components.activity.phoneCall.drawerTitle')}
            </span>
            <Button
              variant="icon"
              icon="cross"
              onClick={onDrawerClose}
              buttonClassName="phone-call-close-button"
            />
          </DrawerHeader>
          <DrawerBody className="phone-call-drawer-body">
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale={datePickerLocale}
              localeText={datePickerLocaleTextMap[datePickerLocale]}
            >
              <FormProvider {...methods}>
                <Form className="phone-call-form">
                  <FormBody>
                    <FormGroup>
                      <FormFields className="phone-call-form-fields">
                        {subjectFieldMap && (
                          <CustomField
                            fieldMap={subjectFieldMap}
                            isEditing={true}
                            className="form-field"
                          />
                        )}
                        <AsyncSelect
                          name="phoneCallPerson"
                          label={t('components.activity.phoneCall.personLabel')}
                          value={selectedPersonId}
                          displayValue={selectedPersonLabel}
                          onChange={onPersonChange}
                          loadReferenceOptions={loadPersonOptions}
                          className="form-field"
                        />
                        <div className="form-field phone-call-type-fields">
                          <CustomField
                            fieldMap={mainTypeFieldMap}
                            isEditing={true}
                            isReadonly
                            className="phone-call-type-input"
                          />
                          <CustomField
                            fieldMap={subTypeFieldMap}
                            isEditing={true}
                            className="phone-call-type-input"
                          />
                        </div>
                        {beginAtField && (
                          <DateTimePicker
                            name={beginAtFieldName}
                            title={t(
                              'components.activity.phoneCall.startTimeLabel',
                            )}
                            requirementState={
                              beginAtField.fieldSchema?.mandatory
                                ? 'required'
                                : 'optional'
                            }
                            onChange={(value) => {
                              setValue(beginAtFieldName, value ?? null, {
                                shouldDirty: true,
                              })
                            }}
                            value={beginAtValue as string | null | undefined}
                            className="form-field"
                            clearable
                            format={dateTimeDisplayFormat}
                          />
                        )}
                        {endAtField && (
                          <div className="form-field phone-call-end-time-field">
                            <FieldLabel
                              name={endAtFieldName}
                              label={endTimeLabel}
                              requirementState={endAtRequirementState}
                            />
                            <DateTimePicker
                              name={endAtFieldName}
                              title={endTimeLabel}
                              requirementState={endAtRequirementState}
                              onChange={(value) => {
                                setIsEndTimeAuto(false)
                                setValue(endAtFieldName, value ?? null, {
                                  shouldDirty: true,
                                })
                              }}
                              value={endAtValue as string | null | undefined}
                              className="phone-call-end-time-input"
                              clearable
                              format={dateTimeDisplayFormat}
                            />
                            <p className="phone-call-end-time-hint">
                              {t('components.activity.phoneCall.endTimeHint')}
                            </p>
                          </div>
                        )}
                        <div className="form-field phone-call-status-field">
                          <FieldLabel
                            name="phoneCallStatus"
                            label={t('form.labels.status')}
                          />
                          <div className="phone-call-status-options">
                            {statusOptionsUi.map((statusOption) => (
                              <label
                                key={statusOption.value}
                                className="phone-call-status-option"
                              >
                                <input
                                  type="radio"
                                  name="phoneCallStatusOption"
                                  className="form-check-input"
                                  checked={
                                    currentStatusValue === statusOption.value
                                  }
                                  onChange={() => setStatus(statusOption.value)}
                                />
                                <span className="form-check-label">
                                  {statusOption.label}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                        {noteField && (
                          <Textarea
                            name={noteField.name}
                            label={t(
                              'components.activity.phoneCall.notesLabel',
                            )}
                            register={register}
                            placeholder={t(
                              'components.activity.phoneCall.notesPlaceholder',
                            )}
                            className="form-field phone-call-notes-field"
                          />
                        )}
                      </FormFields>
                    </FormGroup>
                  </FormBody>
                </Form>
              </FormProvider>
            </LocalizationProvider>
          </DrawerBody>
          <DrawerFooter align="right">
            <Button
              variant="outlined"
              onClick={onDrawerClose}
              size="lg"
              buttonClassName="phone-call-cancel-button"
            >
              {t('common.cancel')}
            </Button>
            <Button
              icon="disk"
              onClick={handleSave}
              size="lg"
              disabled={isSaving}
              buttonClassName="phone-call-save-button"
            >
              {t('common.save')}
            </Button>
          </DrawerFooter>
        </Drawer>
        {followUpOriginActivity && (
          <DataClassFollowUpActivityDrawer
            originActivity={followUpOriginActivity}
            isOpen={isFollowUpDrawerOpen}
            close={() => setIsFollowUpDrawerOpen(false)}
          />
        )}
      </>
    )
  },
)
